import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { lighten } from "@material-ui/core";

import {
  BASE_URL,
  DEFAULT_END_DATE,
  DEFAULT_QUERY_OPTIONS,
  DEFAULT_START_DATE,
  DEFAULT_TIME_INTERVAL,
  ENDPOINT,
  RESERVOIR_NDX,
} from "./constants";

// --- Utility ---
const round = (val) => (val == null ? null : Math.round(val * 100) / 100);

const prepareGraphData = (rows) => {
  if (!rows?.length) {
    return {
      labels: [],
      datasets: [],
    };
  }

  const bySeries = {};
  const labelsSet = new Set();
  const measuredMap = new Map();

  rows.forEach((r) => {
    const ts = new Date(r.collect_date);
    const dateStr = ts.toISOString().slice(0, 10);
    labelsSet.add(dateStr);

    if (!measuredMap.has(dateStr)) {
      measuredMap.set(dateStr, round(r.measured_flow));
    }

    const key = r.cow_input_ndx;
    if (!bySeries[key]) {
      bySeries[key] = {
        label: r.cow_input_name,
        yAxisID: "yL",
        type: "line",
        backgroundColor: r.graph_color,
        borderColor: r.graph_color,
        fill: "origin",
        pointRadius: 0,
        pointHoverRadius: 0,
        spanGaps: false,
        stack: "cow",
        pointStyle: "rect",
        legendAndTooltipOrder: key,
        data: [],
      };
    }

    bySeries[key].data.push(round(r.cow_slice_cfs));
  });

  const labels = Array.from(labelsSet)
    .sort()
    .map((d) => new Date(`${d}T00:00:00`));
  const measured = {
    label: "Measured Flow",
    type: "line",
    yAxisID: "yL",
    backgroundColor: lighten("#000", 0.5),
    borderColor: "#000",
    data: labels.map(
      (d) => measuredMap.get(d.toISOString().slice(0, 10)) ?? null
    ),
    pointBorderWidth: 2,
    pointBackgroundColor: "#0F9ED5",
    pointBorderColor: "#000",
    pointRadius: 4,
    pointHoverRadius: 6,
    borderWidth: 5,
    spanGaps: false,
    stack: false,
    legendAndTooltipOrder: 999,
  };

  return {
    labels,
    datasets: [
      measured,
      ...Object.values(bySeries).sort(
        (a, b) => a.legendAndTooltipOrder - b.legendAndTooltipOrder
      ),
    ],
  };
};

const useFetch = ({ endpoint, queryKey, params = "", options = {} }) => {
  const fetchFn = async () => {
    const { data } = await axios.get(`${BASE_URL}/${endpoint}/${params}`);
    return data;
  };

  return useQuery(queryKey ?? [endpoint], fetchFn, {
    onError: (err) => console.error(err),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// --- Main Hook ---
export const useColorsOfWaterStagecoach = () => {
  const [timeInterval, setTimeInterval] = useState(DEFAULT_TIME_INTERVAL);
  const [streamGage, setStreamGage] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: DEFAULT_START_DATE,
    endDate: DEFAULT_END_DATE,
  });

  // --- Event Handlers ---
  const handleTimeInterval = useCallback(
    (e) => setTimeInterval(e.target.value),
    []
  );
  const handleStreamGage = useCallback(
    (e) => setStreamGage(e.target.value),
    []
  );
  const handleStartDate = useCallback(
    (date) => setDateRange((prev) => ({ ...prev, startDate: date })),
    []
  );
  const handleEndDate = useCallback(
    (date) => setDateRange((prev) => ({ ...prev, endDate: date })),
    []
  );

  // --- Stream Gages List ---
  const { data: dataListStreamGages } = useFetch({
    endpoint: `${ENDPOINT}/stream-gages`,
    queryKey: ["stream-gages", RESERVOIR_NDX],
    params: RESERVOIR_NDX,
    options: {
      select: (data) =>
        data.map((item) => ({
          value: item.stream_gage_ndx,
          label: item.stream_gage,
        })),
    },
  });

  useEffect(() => {
    if (!streamGage && dataListStreamGages?.length) {
      setStreamGage(dataListStreamGages[dataListStreamGages.length - 1].value);
    }
  }, [streamGage, dataListStreamGages]);

  // --- Graph Data ---
  const {
    data,
    error,
    isFetching: isLoading,
    refetch,
  } = useFetch({
    endpoint: `${ENDPOINT}/graph-${timeInterval}`,
    queryKey: [
      "line",
      streamGage,
      dateRange.startDate.toISOString().slice(0, 10),
      dateRange.endDate.toISOString().slice(0, 10),
    ],
    params: `${streamGage}/${dateRange.startDate
      .toISOString()
      .slice(0, 10)}/${dateRange.endDate.toISOString().slice(0, 10)}`,
    options: {
      enabled: !!streamGage,
    },
  });

  const graphData = useMemo(() => prepareGraphData(data), [data]);

  return {
    streamGage,
    dataListStreamGages,
    handleStreamGage,
    timeInterval,
    setTimeInterval,
    handleTimeInterval,
    dateRange,
    setDateRange,
    handleStartDate,
    handleEndDate,
    rawData: data,
    data: graphData,
    error,
    isLoading,
    refetch,
  };
};
